import React, { useCallback, useEffect, useState, createElement } from 'react';
import { RotateCcwIcon, SearchIcon, CalendarIcon, EyeIcon, PrinterIcon, PackageIcon, BuildingIcon, UserIcon, BanIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { Visit } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import jsPDF from 'jspdf';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
async function printReciboDevolucion(visit: Visit, codigoCliente: string) {
  const pw = 80;
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr;
  const cx = pw / 2;
  const items = visit.devolucionItems || [];
  const totalUnidades = items.reduce((sum, item) => sum + item.cantidad, 0);
  // Calculate dynamic height based on items
  const baseHeight = 160;
  const rowHeight = 18; // each product takes ~18mm (name + sku + details)
  const ph = Math.max(baseHeight + items.length * rowHeight, 200);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, ph]
  });
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const fmtDateLong = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  const dashedLine = (y: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, y, pw - mr, y);
    doc.setLineDashPattern([], 0);
  };
  let y = 2;
  // ── LOGO + HEADER ─────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av. Antonio Guzmán No.102 • 809-247-1006', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('Santiago, Rep. Dom.', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── TITLE ─────────────────────────────────────────────────────────────────
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 30, 30);
  doc.text('RECIBO DE DEVOLUCIÓN', cx, y, {
    align: 'center'
  });
  doc.setTextColor(0, 0, 0);
  y += 6;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = 'DEV-' + visit.id.replace('temp-', '').replace(/-/g, '').slice(-4).toUpperCase();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('No: ' + receiptNum, ml, y);
  doc.setFont('helvetica', 'normal');
  doc.text('Fecha: ' + fmtDate(visit.fecha), pw - mr, y, {
    align: 'right'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── CLIENT & VENDEDOR ─────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Cliente:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(visit.clienteNombre, ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Código:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(codigoCliente || '-', ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Zona:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.zona || '-', ml + 14, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Vendedor:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.vendedorNombre, ml + 18, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Motivo: Productos vencidos / devolución', ml, y);
  y += 5;
  dashedLine(y);
  y += 4;
  // ── PRODUCTS TABLE ────────────────────────────────────────────────────────
  // Header — white background with bold black text for thermal printer compatibility
  const tRowH = 5.5;
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  // Columns: PRODUCTO (30) | CANT (10) | LOTE (16) | VENCE (16) = 72
  const colWidths = [30, 10, 16, 16];
  const colHeaders = ['PRODUCTO', 'CANT', 'LOTE', 'VENCE'];
  let tx = ml;
  colHeaders.forEach((col, i) => {
    doc.text(col, tx + colWidths[i] / 2, y + tRowH - 1.2, {
      align: 'center'
    });
    tx += colWidths[i];
  });
  y += tRowH;
  // Data rows
  doc.setTextColor(0, 0, 0);
  items.forEach((item, idx) => {
    const isEven = idx % 2 === 0;
    const nameRowH = 5;
    const skuRowH = 3.5;
    const totalItemH = nameRowH + skuRowH + 1;
    // Background
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, totalItemH, 'F');
    doc.setDrawColor(220, 225, 230);
    doc.setLineWidth(0.1);
    doc.line(ml, y + totalItemH, pw - mr, y + totalItemH);
    // Product name (truncated to fit)
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    const maxNameWidth = colWidths[0] - 2;
    let displayName = item.productoNombre;
    while (doc.getTextWidth(displayName) > maxNameWidth && displayName.length > 3) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName !== item.productoNombre) displayName += '..';
    doc.text(displayName, ml + 1, y + nameRowH - 0.5);
    // SKU below name
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(item.productoSku, ml + 1, y + nameRowH + skuRowH - 1);
    doc.setTextColor(0, 0, 0);
    // Cantidad - centered, bold
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(String(item.cantidad), ml + colWidths[0] + colWidths[1] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Lote
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text(item.lote || '-', ml + colWidths[0] + colWidths[1] + colWidths[2] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Fecha vencimiento
    doc.text(item.fechaVencimiento ? fmtDateLong(item.fechaVencimiento) : '-', ml + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    y += totalItemH;
  });
  // ── TOTAL ROW ─────────────────────────────────────────────────────────────
  const totalRowH = 7;
  doc.setFillColor(240, 245, 255);
  doc.rect(ml, y, cw, totalRowH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, totalRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL UNIDADES:', ml + 2, y + totalRowH - 2);
  doc.setFontSize(10);
  doc.text(String(totalUnidades), pw - mr - 2, y + totalRowH - 1.5, {
    align: 'right'
  });
  doc.setTextColor(0, 0, 0);
  y += totalRowH + 4;
  dashedLine(y);
  y += 7;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Entregado (Cliente)', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('Recibido (Vendedor)', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 10;
  dashedLine(y);
  y += 6;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Devolución Registrada ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY = y;
  const barH = 10;
  let bx = ml;
  const seed = receiptNum.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w = seed * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx, barY, w, barH, 'F');
    }
    bx += w + 0.3;
    if (bx > ml + cw - 2) break;
  }
  y += barH + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: ' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function DevolucionesPage() {
  const {
    clients
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [anularVisit, setAnularVisit] = useState<Visit | null>(null);
  const [isAnulando, setIsAnulando] = useState(false);
  // ── Fetch visits with devoluciones directly from DB ─────────────────────
  // The global visits from AppContext no longer include devolucionItems (egress optimization).
  // This page fetches ONLY visits that have devoluciones — a small, targeted query.
  const [devVisits, setDevVisits] = useState<Visit[]>([]);
  const [isLoadingDev, setIsLoadingDev] = useState(true);
  const fetchDevoluciones = useCallback(async () => {
    setIsLoadingDev(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('visits').select('id,vendedor_id,vendedor_nombre,cliente_id,cliente_nombre,zona,tipo_visita,fecha,hora_llegada,hora_salida,tiempo_total,estado,numero_recibo,devolucion_items,devolucion_anulada,productos_vencidos').not('devolucion_items', 'eq', '[]').order('created_at', {
        ascending: false
      }).limit(200);
      if (data) {
        const mapped: Visit[] = data.filter((row: any) => row.devolucion_items && row.devolucion_items.length > 0).map((row: any): Visit => ({
          id: row.id,
          vendedorId: row.vendedor_id,
          vendedorNombre: row.vendedor_nombre,
          clienteId: row.cliente_id,
          clienteNombre: row.cliente_nombre,
          zona: row.zona,
          tipoVisita: row.tipo_visita,
          fecha: new Date(row.fecha),
          horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
          horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
          tiempoTotal: row.tiempo_total,
          cobro: {
            huboCobo: false,
            montoCobrado: 0,
            metodoPago: null,
            numeroFactura: '',
            estadoFactura: 'no_pago',
            credito: 0,
            descuento: 0
          },
          venta: {
            huboVenta: false,
            montoVenta: 0,
            numeroPedido: ''
          },
          comentarios: '',
          motivoNoPago: '',
          productosVencidos: row.productos_vencidos ?? null,
          devolucionItems: row.devolucion_items || [],
          proximaVisita: null,
          estado: row.estado,
          numeroRecibo: row.numero_recibo || null,
          devolucionAnulada: row.devolucion_anulada || false
        }) as any);
        setDevVisits(mapped);
      }
    } catch (err) {
      console.error('Error loading devoluciones:', err);
    } finally {
      setIsLoadingDev(false);
    }
  }, []);
  useEffect(() => {
    fetchDevoluciones();
  }, [fetchDevoluciones]);
  const [anulados, setAnulados] = useState<Set<string>>(() => {
    const set = new Set<string>();
    devVisits.forEach(v => {
      if ((v as any).devolucionAnulada) set.add(v.id);
    });
    return set;
  });
  // Update anulados when devVisits loads
  useEffect(() => {
    const set = new Set<string>();
    devVisits.forEach(v => {
      if ((v as any).devolucionAnulada) set.add(v.id);
    });
    setAnulados(set);
  }, [devVisits]);
  const handleAnularDevolucion = async () => {
    if (!anularVisit) return;
    setIsAnulando(true);
    try {
      await supabaseAdmin.from('visits').update({
        devolucion_anulada: true
      }).eq('id', anularVisit.id);
      setAnulados(prev => new Set([...prev, anularVisit.id]));
      addActivityLog('Anuló devolución', `Cliente: ${anularVisit.clienteNombre} | Productos: ${anularVisit.devolucionItems?.length || 0}`);
      setAnularVisit(null);
    } catch (err) {
      console.error('Error anulando devolución:', err);
    } finally {
      setIsAnulando(false);
    }
  };
  const ITEMS_PER_PAGE = 10;
  // Filter visits that have devoluciones — now using local devVisits
  const devolucionesVisits = devVisits.filter(v => v.devolucionItems && v.devolucionItems.length > 0);
  // Apply search and role filters
  const filteredVisits = devolucionesVisits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return v.clienteNombre.toLowerCase().includes(query) || v.vendedorNombre.toLowerCase().includes(query) || v.devolucionItems?.some(item => item.productoNombre.toLowerCase().includes(query) || item.productoSku.toLowerCase().includes(query));
    }
    return true;
  });
  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    cliente: v => v.clienteNombre,
    vendedor: v => v.vendedorNombre,
    cantidad: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0,
    valor: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0
  });
  // Default sort by date descending if no sort config
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const handlePrintRecibo = async (visit: Visit) => {
    setPrintingId(visit.id);
    try {
      const client = clients.find(c => c.id === visit.clienteId);
      const codigoCliente = client?.codigo || client?.id?.slice(0, 8) || '-';
      await printReciboDevolucion(visit, codigoCliente);
    } finally {
      setPrintingId(null);
    }
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-2653" data-mp="3t13bxk3fxf21">
      <PageHeader title="Devoluciones" subtitle="Registro de productos devueltos o vencidos" icon={<RotateCcwIcon className="w-6 h-6" data-id="element-2655" data-mp="3k3t1g0yg5ty6" />} data-id="element-2654" data-mp="1pac0iu768d0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto" data-id="element-2656" data-mp="3o3jygmekh6b5">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-2658" data-mp="kltfosm8f426" />} data-id="element-2657" data-mp="fo075sxghxyq" />
          <div className="relative w-full sm:w-64" data-id="element-2659" data-mp="6nllutkhsiv8">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2660" data-mp="6mtobd7c4b2b" />
            <input type="text" placeholder="Buscar cliente o producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-2661" data-mp="bklh09ft017k" />
          </div>
        </div>
      </PageHeader>

      {/* Table */}
      <Card padding="none" className="animate-stagger-1" data-id="element-2662" data-mp="3e1stv2o6b39p">
        {filteredVisits.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-2663" data-mp="393hfqfqui6lo">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-rose-50 rounded-2xl mb-4" data-id="element-2664" data-mp="395aoc0l0r0wb">
              <RotateCcwIcon className="w-7 h-7 sm:w-8 sm:h-8 text-rose-400" data-id="element-2665" data-mp="5mhbjq02eg37" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-2666" data-mp="v96t79ojiwd">
              No hay devoluciones
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2667" data-mp="3s10s9tcii7cu">
              {dateFilter || searchQuery ? 'No se encontraron devoluciones con los filtros aplicados' : 'Aún no hay devoluciones registradas en el sistema'}
            </p>
          </div> : <>
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100" data-id="element-2668" data-mp="10vcn8fk8pa4h">
              {paginatedVisits.map(visit => {
            const totalItems = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0;
            const uniqueProducts = visit.devolucionItems?.length || 0;
            const totalVal = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0;
            return <div key={visit.id} className="p-3.5 active:bg-rose-50/30 transition-colors" onClick={() => setSelectedVisit(visit)} data-id="element-2669" data-mp="1tioz9vsrxzjj">
                    <div className="flex items-start justify-between gap-2" data-id="element-2670" data-mp="22ky7ug4hfiua">
                      <div className="min-w-0 flex-1" data-id="element-2671" data-mp="3223n3svmmzp6">
                        <p className="font-semibold text-slate-900 text-sm" data-id="element-2672" data-mp="36zvb4bt5i1o3">
                          {visit.clienteNombre}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-2673" data-mp="3bxes3jh8gsjq">
                          {formatDate(visit.fecha)} ·{' '}
                          {formatTime(visit.horaLlegada)}
                        </p>
                        {isAdmin && <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-2674" data-mp="3pyhczswhrwt2">
                            {visit.vendedorNombre}
                          </p>}
                      </div>
                      <div className="flex-shrink-0 text-right" data-id="element-2675" data-mp="3jodon1bx0nr">
                        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 text-rose-700" data-id="element-2676" data-mp="3kzcs9jtxk8j">
                          <PackageIcon className="w-3.5 h-3.5" data-id="element-2677" data-mp="cjl2qprms1jc" />
                          <span className="font-bold text-sm" data-id="element-2678" data-mp="chrx9dsr7ncx">
                            {totalItems}
                          </span>
                          <span className="text-[10px] opacity-80" data-id="element-2679" data-mp="1s2ao4b9ozp9w">
                            ({uniqueProducts})
                          </span>
                        </div>
                        {totalVal > 0 && <p className="text-xs font-bold text-rose-700 mt-1" data-id="element-2680" data-mp="1j35rjahoy60d">
                            RD$
                            {totalVal.toLocaleString('es-DO', {
                      minimumFractionDigits: 2
                    })}
                          </p>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-2681" data-mp="2zgm42p84af27">
              <table className="w-full" data-id="element-2682" data-mp="2zi724otn4g4t">
                <thead data-id="element-2683" data-mp="2uomadein4ie2">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2684" data-mp="2uo2tm14q801m">
                    <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2685" data-mp="3dmxkgkvpe5iq" />
                    <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2686" data-mp="38u4ejtoi0r52" />
                    {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2687" data-mp="2lwt36v38pylq" />}
                    <SortableTableHeader label="Total Productos" sortKey="cantidad" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2688" data-mp="28mhgwcephahn" />
                    <SortableTableHeader label="Valor" sortKey="valor" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2689" data-mp="2dkrd8qc62h4u" />
                    <th className="px-5 py-4 w-24" data-id="element-2690" data-mp="2wck4ogbl7uag"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-2691" data-mp="16xne8jv510zn">
                  {paginatedVisits.map(visit => {
                const totalItems = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0;
                const uniqueProducts = visit.devolucionItems?.length || 0;
                const isPrinting = printingId === visit.id;
                return <tr key={visit.id} className="hover:bg-rose-50/30 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-2692" data-mp="1icyt8ax51tkj">
                        <td className="px-5 py-4" data-id="element-2693" data-mp="pp31lkmferum">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-2694" data-mp="uneb1u8t70cb">
                            {formatDate(visit.fecha)}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-2695" data-mp="zfp90rjzoc28">
                            {formatTime(visit.horaLlegada)}
                          </p>
                        </td>
                        <td className="px-5 py-4" data-id="element-2696" data-mp="6ws5qaq6vu60">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-2697" data-mp="3vjoq8mq13vvz">
                            {visit.clienteNombre}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-2698" data-mp="3ql53ik3m2g2k">{visit.zona}</p>
                        </td>
                        {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-2699" data-mp="3gmftfdhyhzwv">
                            {visit.vendedorNombre}
                          </td>}
                        <td className="px-5 py-4 text-center" data-id="element-2700" data-mp="3cmnl91wh5vs1">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700" data-id="element-2701" data-mp="37tcd8xfxf8yj">
                            <PackageIcon className="w-3.5 h-3.5" data-id="element-2702" data-mp="32swzw5vna1gk" />
                            <span className="font-bold text-sm" data-id="element-2703" data-mp="2xx98djtoww7w">
                              {totalItems}
                            </span>
                            <span className="text-xs opacity-80" data-id="element-2704" data-mp="2syo7rjaj0aix">
                              ({uniqueProducts} ref)
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-2705" data-mp="2j8zhg18f7g7v">
                          {(() => {
                      const totalVal = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0;
                      return totalVal > 0 ? <span className="font-bold text-rose-700 text-sm" data-id="element-2706" data-mp="2o7j5q8ahl43e">
                                RD$
                                {totalVal.toLocaleString('es-DO', {
                          minimumFractionDigits: 2
                        })}
                              </span> : <span className="text-slate-300 text-sm" data-id="element-2707" data-mp="3v2i6lxazzfwf">—</span>;
                    })()}
                        </td>
                        <td className="px-5 py-4" data-id="element-2708" data-mp="3r0ocy8z7zc5r">
                          <div className="flex items-center gap-1" data-id="element-2709" data-mp="8ngpcl4uuo92">
                            <Button variant="ghost" size="sm" onClick={e => {
                        e.stopPropagation();
                        setSelectedVisit(visit);
                      }} data-id="element-2710" data-mp="8ortim27py0p">
                              <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-2711" data-mp="3m2v6rfxx2i5h" />
                            </Button>
                            {!anulados.has(visit.id) && <>
                                <button onClick={e => {
                          e.stopPropagation();
                          handlePrintRecibo(visit);
                        }} disabled={isPrinting} title="Imprimir recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-40" data-id="element-2712" data-mp="11ajj6zyl6asz">
                                  {isPrinting ? <div className="w-4 h-4 border-2 border-rose-200 border-t-rose-600 rounded-full animate-spin" data-id="element-2713" data-mp="39i6eo1apgp17" /> : <PrinterIcon className="w-4 h-4" data-id="element-2714" data-mp="3ije9uahxd9r2" />}
                                </button>
                                <button onClick={e => {
                          e.stopPropagation();
                          setAnularVisit(visit);
                        }} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2715" data-mp="3nih5x5fxg5mw">
                                  <BanIcon className="w-4 h-4" data-id="element-2716" data-mp="1buvuj700herx" />
                                </button>
                              </>}
                            {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-2717" data-mp="24jsy03zkd70m">
                                <BanIcon className="w-3 h-3" data-id="element-2718" data-mp="1ulvdnrktruda" /> Anulado
                              </span>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </>}
      </Card>

      <div className="animate-stagger-2" data-id="element-2719" data-mp="f43axzdvd4le">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2720" data-mp="3a1vghxyfllhx" />
      </div>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Devolución" size="lg" data-id="element-2721" data-mp="3ju1b7fg2ih0t">
        {selectedVisit && <>
            <ModalBody data-id="element-2722" data-mp="3suplbz2cbx8f">
              <div className="space-y-6" data-id="element-2723" data-mp="3orb63am87kw1">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4" data-id="element-2724" data-mp="3otdtrnzwp1xl">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-2725" data-mp="6k3lsbswung1">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-2726" data-mp="1muzbz0qbx8z">
                      <BuildingIcon className="w-5 h-5 text-indigo-600" data-id="element-2727" data-mp="28emd28c4hj44" />
                    </div>
                    <div data-id="element-2728" data-mp="23g1bo5l4wte3">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-2729" data-mp="iv4atkj1b7gg">
                        Cliente
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-2730" data-mp="ntfo658jg03f">
                        {selectedVisit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2731" data-mp="e4ny62n1fpv5">
                        {selectedVisit.zona}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-2732" data-mp="7iweeucxd5p">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-2733" data-mp="3mce5s02op95o">
                      <UserIcon className="w-5 h-5 text-emerald-600" data-id="element-2734" data-mp="3rao0k9khy7qr" />
                    </div>
                    <div data-id="element-2735" data-mp="1kkhr4hl7zj6o">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-2736" data-mp="1kmb2292v8pkh">
                        Vendedor
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-2737" data-mp="1phqdkudei9dq">
                        {selectedVisit.vendedorNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2738" data-mp="1j2t165u8fyjh">
                        {formatDate(selectedVisit.fecha)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Products List */}
                <div data-id="element-2739" data-mp="26tf4xkx7ewno">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-2740" data-mp="26tybk5helyme">
                    <PackageIcon className="w-5 h-5 text-rose-500" data-id="element-2741" data-mp="21ulnxiipknir" />
                    Productos Devueltos
                  </h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2742" data-mp="1x2aripn6fjuy">
                    <table className="w-full text-sm" data-id="element-2743" data-mp="1t2thdiwt1yjg">
                      <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2744" data-mp="1t07broth5t3v">
                        <tr data-id="element-2745" data-mp="2pl7q4t617dku">
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2746" data-mp="2kt7q70pyoko9">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-600" data-id="element-2747" data-mp="7cegz9cy2ymn">
                            Cantidad
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-2748" data-mp="gayt1ro02a1w">
                            Precio Unit.
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-2749" data-mp="l7yrphiisaiz">
                            Valor
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2750" data-mp="v77o0h0uy18i">
                            Lote
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2751" data-mp="140kr2bsmyj8b">
                            Vencimiento
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2752" data-mp="1dy7c7g0j370q">
                        {selectedVisit.devolucionItems?.map((item, idx) => {
                      const precio = (item as any).precioUnitario || 0;
                      const valor = item.cantidad * precio;
                      return <tr key={idx} className="bg-white" data-id="element-2753" data-mp="1a1joogucg9mz">
                              <td className="px-4 py-3" data-id="element-2754" data-mp="152gkeym6u7g2">
                                <p className="font-semibold text-slate-900" data-id="element-2755" data-mp="155305lj8ft9d">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-2756" data-mp="cfmagvmzyqgu">
                                  {item.productoSku}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-center" data-id="element-2757" data-mp="maxg8ogfgncg">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-700 font-bold" data-id="element-2758" data-mp="22pz24yxtujw7">
                                  {item.cantidad}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600" data-id="element-2759" data-mp="2aa9ncyv6b941">
                                {precio > 0 ? `RD${precio.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-rose-700" data-id="element-2760" data-mp="2o3r6iijy6jtj">
                                {valor > 0 ? `RD${valor.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700 font-medium" data-id="element-2761" data-mp="36z8vmdvrcaw8">
                                {item.lote || '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700" data-id="element-2762" data-mp="3lm7ng9yj17bh">
                                {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : '-'}
                              </td>
                            </tr>;
                    })}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-2763" data-mp="2oxrm7gm219ij">
                        <tr data-id="element-2764" data-mp="2jyexeqbmhsdb">
                          <td className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-2765" data-mp="2jwkbb8om7u7n">
                            Totales:
                          </td>
                          <td className="px-4 py-3 text-center font-black text-rose-600 text-base" data-id="element-2766" data-mp="24uvlv341zhe7">
                            {selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0)}
                          </td>
                          <td className="px-4 py-3" data-id="element-2767" data-mp="2jse89yt9diih"></td>
                          <td className="px-4 py-3 text-right font-black text-rose-700 text-base" data-id="element-2768" data-mp="2jps3u81nzj4h">
                            RD$
                            {(selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0).toLocaleString('es-DO', {
                          minimumFractionDigits: 2
                        })}
                          </td>
                          <td colSpan={2} data-id="element-2769" data-mp="2mlijwc1xsq1x"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter data-id="element-2770" data-mp="2riipkluvjqsi">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-2771" data-mp="2ri985mnwaryf">
                Cerrar
              </Button>
              <Button onClick={() => handlePrintRecibo(selectedVisit)} disabled={printingId === selectedVisit.id} leftIcon={printingId === selectedVisit.id ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-2773" data-mp="3aa1q8m1fesrd" /> : <PrinterIcon className="w-4 h-4" data-id="element-2774" data-mp="3p6j3xjx38jh3" />} className="bg-rose-600 hover:bg-rose-700 text-white" data-id="element-2772" data-mp="31gorv4kleyxw">
                Imprimir Recibo
              </Button>
            </ModalFooter>
          </>}
      </Modal>

      {/* Anular Recibo Modal */}
      <Modal isOpen={!!anularVisit} onClose={() => setAnularVisit(null)} title="Anular Recibo de Devolución" size="sm" data-id="element-2775" data-mp="3tusiy1mtilmy">
        <ModalBody data-id="element-2776" data-mp="2mxt1qi361i8">
          <div className="text-center space-y-3" data-id="element-2777" data-mp="2qlncrvcury0">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-2778" data-mp="3k4qqkxnfmh28">
              <BanIcon className="w-7 h-7 text-rose-500" data-id="element-2779" data-mp="3owqz3ehkiiaf" />
            </div>
            <p className="text-slate-600" data-id="element-2780" data-mp="3b4kg7kn15x7a">
              ¿Estás seguro de anular el recibo de devolución de{' '}
              <strong data-id="element-2781" data-mp="3g2m3khrhoiok">{anularVisit?.clienteNombre}</strong>?
            </p>
            <p className="text-xs text-slate-400" data-id="element-2782" data-mp="316eizgc65f8r">
              Esta acción marcará el recibo como anulado. No se puede deshacer.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2783" data-mp="36728tyonvq1e">
          <Button variant="secondary" onClick={() => setAnularVisit(null)} data-id="element-2784" data-mp="11c4he1w6dot">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularDevolucion} isLoading={isAnulando} leftIcon={<BanIcon className="w-4 h-4" data-id="element-2786" data-mp="3ngd9nrurfevt" />} data-id="element-2785" data-mp="60evvvoy8leb">
            Anular Recibo
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}